import { useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";

/**
 * useAviStream(handlers) — hook client pour l'endpoint SSE `/api/avi/stream`.
 *
 * @param handlers  Objet { onCreated, onDecision, onCancelled, onReminder, onAny }
 *                  — chaque handler reçoit l'objet event JSON parsé.
 * @param opts      { enabled: boolean } — désactive l'ouverture si false.
 *
 * L'`EventSource` natif ne supporte pas les headers, on passe donc le JWT
 * via query param. Reconnexion automatique par le browser en cas de perte.
 *
 * Le hook déclenche aussi `window.dispatchEvent(new Event('avi:badge-pulse'))`
 * à chaque événement — la Sidebar écoute ce signal pour animer le badge.
 */
export default function useAviStream(handlers = {}, { enabled = true } = {}) {
  const { user } = useAuth();
  const handlersRef = useRef(handlers);
  useEffect(() => { handlersRef.current = handlers; }, [handlers]);

  useEffect(() => {
    if (!enabled || !user) return undefined;
    const token = sessionStorage.getItem("ledesk_token");
    if (!token) return undefined;
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const url = `${base}/api/avi/stream?token=${encodeURIComponent(token)}`;

    let closed = false;
    let es;
    try {
      es = new EventSource(url);
    } catch (e) {
      return undefined;
    }

    const dispatchPulse = () => {
      try { window.dispatchEvent(new Event("avi:badge-pulse")); } catch {}
    };

    const wrap = (name, handlerKey) => (evt) => {
      if (closed) return;
      try {
        const data = JSON.parse(evt.data);
        dispatchPulse();
        handlersRef.current?.[handlerKey]?.(data);
        handlersRef.current?.onAny?.(data);
      } catch (e) { /* silent */ }
    };

    es.addEventListener("avi.created",  wrap("avi.created",  "onCreated"));
    es.addEventListener("avi.decision", wrap("avi.decision", "onDecision"));
    es.addEventListener("avi.cancelled",wrap("avi.cancelled","onCancelled"));
    es.addEventListener("avi.reminder", wrap("avi.reminder", "onReminder"));
    // Spec rev159 (2026-02-07) — événements corbeille (Lot A-bis).
    es.addEventListener("avi.trashed",  wrap("avi.trashed",  "onTrashed"));
    es.addEventListener("avi.restored", wrap("avi.restored", "onRestored"));

    es.onerror = () => {
      // Le browser reconnecte automatiquement. On log en debug seulement.
      // Éviter le noise console.
    };

    return () => {
      closed = true;
      try { es.close(); } catch {}
    };
  }, [enabled, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
}
